import axios from 'axios';
import type { CreateAxiosClientOptions, AxiosClientResult } from './types/config';
import type { AuthStoreAdapter } from './types/auth';
import { mergeConfig } from './config/merger';
import { createClientState } from './state/clientState';
import { refreshAccessToken } from './token/refresh';
import { handleTokenRefresh } from './token/handler';
import { retryQueuedRequests } from './queue/retry';
import { setupInterceptors } from './interceptors/setup';

/**
 * Validates that the auth store adapter implements all required methods correctly.
 * This catches configuration errors at startup rather than at runtime during auth flows.
 *
 * @param authStore The auth store adapter to validate
 * @throws Error if adapter is missing methods or has incorrect signatures
 */
function validateAuthStoreAdapter(authStore: AuthStoreAdapter): void {
  const requiredMethods = ['getToken', 'getRefreshToken', 'setTokens', 'logout'];
  const missingMethods: string[] = [];

  for (const method of requiredMethods) {
    if (typeof authStore[method as keyof AuthStoreAdapter] !== 'function') {
      missingMethods.push(method);
    }
  }

  if (missingMethods.length > 0) {
    throw new Error(
      `AuthStoreAdapter is missing required methods: ${missingMethods.join(', ')}. ` +
      `Please ensure your auth adapter implements all methods from the AuthStoreAdapter interface.`
    );
  }

  // Test that setTokens has the correct signature (takes 2 params)
  try {
    const setTokensLength = authStore.setTokens.length;
    if (setTokensLength !== 2) {
      console.warn(
        `⚠️ AuthStoreAdapter.setTokens expects 2 parameters (token, refreshToken), but function has ${setTokensLength} parameters. ` +
        `This may cause runtime errors during token refresh.`
      );
    }
  } catch (error) {
    // Ignore if we can't check function length (shouldn't happen)
  }

  console.log('✅ AuthStoreAdapter validation passed');
}

/**
 * Factory function to create an axios client with authentication interceptors.
 *
 * Features:
 * - Proactive token refresh when token is near expiry
 * - Automatic retry on 401 with token refresh
 * - 403 permission refresh (ONE attempt for dynamically granted permissions)
 * - Network error retry with exponential backoff (configurable per error type)
 * - Request queueing during token refresh
 * - Event dispatching for network errors and permission refreshes
 * - Distinction between auth failures (logout) and infrastructure failures (retry)
 *
 * @param options Configuration options
 * @returns Object containing authenticated and public axios instances
 *
 * @example
 * ```typescript
 * const { authenticatedInstance, publicInstance } = createAxiosClient({
 *   baseURL: 'http://localhost:6550',
 *   authStore: myAuthAdapter,
 *   onAuthFailure: (details) => {
 *     console.error('Auth failed:', details);
 *     navigate('/login');
 *   },
 *   onNetworkError: (message, retryQueue, isColdStart) => {
 *     window.dispatchEvent(new CustomEvent('auth:network-error', {
 *       detail: { message, retryQueue, isColdStart }
 *     }));
 *   },
 * });
 * ```
 */
export function createAxiosClient(
  options: CreateAxiosClientOptions
): AxiosClientResult {
  const {
    baseURL,
    authStore,
    onAuthFailure,
    onNetworkError,
    config: userConfig,
    testMode = false,
    forceInterceptors = false,
    clientType,
    appVersion = 'unknown',
    getConnectionType,
  } = options;

  // Validate auth store adapter (catches config errors early)
  validateAuthStoreAdapter(authStore);

  // Merge user config with defaults
  const config = mergeConfig(userConfig);

  // Create client state (manages refresh promise and request queues)
  const state = createClientState();

  // Common headers for version tracking (applied to all requests)
  const versionHeaders = {
    'X-Client-Version': appVersion,
    'X-Client-Type': clientType,
  };

  // Create axios instances with version headers
  const authenticatedInstance = axios.create({
    baseURL,
    timeout: config.timeouts.defaultRequest,
    headers: versionHeaders,
  });

  const publicInstance = axios.create({
    baseURL,
    timeout: config.timeouts.defaultRequest,
    headers: versionHeaders,
  });

  // Create bound handler functions that close over dependencies
  // These are hoisted so handleTokenRefresh can be returned for external use
  let boundHandleTokenRefresh: (() => Promise<string>) | null = null;

  // Setup interceptors (unless in test mode without forceInterceptors)
  if (!testMode || forceInterceptors) {
    const boundRefreshAccessToken = () =>
      refreshAccessToken({
        baseURL,
        authStore,
        config,
        retryQueuedRequests: boundRetryQueuedRequests,
        onNetworkError,
        clientType,
        appVersion,
      });

    boundHandleTokenRefresh = () =>
      handleTokenRefresh({
        state,
        authenticatedInstance,
        onAuthFailure,
        onNetworkError,
        retryQueuedRequests: boundRetryQueuedRequests,
        refreshAccessToken: boundRefreshAccessToken,
        baseURL,
        authStore,
        config,
        clientType,
        appVersion,
      });

    const boundRetryQueuedRequests = () =>
      retryQueuedRequests({
        state,
        authenticatedInstance,
        handleTokenRefresh: boundHandleTokenRefresh!,
      });

    // Setup interceptors with all dependencies
    setupInterceptors({
      authenticatedInstance,
      authStore,
      state,
      config,
      handleTokenRefresh: boundHandleTokenRefresh,
      onAuthFailure,
      onNetworkError,
      retryQueuedRequests: boundRetryQueuedRequests,
      baseURL,
      refreshAccessToken: boundRefreshAccessToken,
      clientType,
      appVersion,
      getConnectionType,
    });
  }

  return {
    authenticatedInstance,
    publicInstance,
    handleTokenRefresh: boundHandleTokenRefresh,
  };
}
